import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { APP_HOST } from '@utils/apiHooks';
import { buildTargetGroupsItems } from '@utils/targetGroupsUtils';
import { iconsMap } from '@composites/PDF/constants';
export interface WhoAndWhyPageProps {
  event: PublicEventDetailsResponse;
  footer: JSX.Element;
}

export interface TargetGroupItem {
  title: string;
  icon: string;
  content: Array<{ percentage: number; title: string }>;
}

const WhoAndWhyPage = ({ event, footer }: WhoAndWhyPageProps) => {
  const { t } = useTranslation();

  const eventHomePageUrl = `${APP_HOST}/events/${event?.slug}`;

  const eventStatisticsItems = useMemo(() => {
    const raw = (event as any)?.website_content?.statistics;

    const arr: any[] = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? (() => {
            try {
              const p = JSON.parse(raw);
              return Array.isArray(p)
                ? p
                : p && typeof p === 'object'
                  ? Object.values(p)
                  : [];
            } catch {
              return [];
            }
          })()
        : raw && typeof raw === 'object'
          ? Object.values(raw)
          : [];

    return arr
      .filter(Boolean)
      .map((s: any) => ({
        description: String(s?.description ?? ''),
        title: String(s?.value ?? ''),
        icon: String(s?.icon ?? '').replace(/-/g, '_') || 'circle_info',
        order: Number(s?.order ?? 0),
      }))
      .sort((a, b) => a.order - b.order);
  }, [event?.website_content?.statistics])?.slice(0, 9);

  const targetGroupsItems = useMemo<TargetGroupItem[]>(
    () => buildTargetGroupsItems((event as any)?.website_content?.targetgroups),
    [event?.website_content?.targetgroups]
  );

  const targetGroupItemWidth = targetGroupsItems.length > 4 ? '31%' : '48%';

  return (
    <Page
      size={{ width: 1440, height: 800 }}
      style={{ ...styles.page, overflow: 'hidden' }}
    >
      <View
        style={{
          ...styles.leftColumn,
        }}
      >
        <Text style={{ ...styles.title }}>{t('who-&-why')}</Text>
        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor ?? 'black',
            marginTop: 20,
            marginLeft: 0,
          }}
        />

        <Text style={styles.about}>
          {t('who-&-why-pdf-text-1')}
          <Link
            src={eventHomePageUrl}
            style={{ textDecoration: 'none', color: '#27343d' }}
          >
            {t('who-&-why-pdf-text-2', { eventName: event?.name })}
          </Link>
        </Text>

        <View style={styles.statisticsGrid}>
          {eventStatisticsItems?.length > 0 &&
            eventStatisticsItems?.map((item, index) => {
              return (
                <View key={index} style={styles.statisticsItem}>
                  <Text style={{ ...styles.icon, marginTop: 5 }}>
                    {iconsMap[item?.icon] || iconsMap.default}
                  </Text>
                  <View style={styles.textWrapper}>
                    <Text style={styles.statTitle}>{item.title}</Text>
                    <Text style={styles.statDescription}>
                      {item.description}
                    </Text>
                  </View>
                </View>
              );
            })}
        </View>
      </View>

      <View
        style={{
          ...styles.rightColumn,
          backgroundColor: event?.design?.themeColor,
        }}
      >
        <Text style={{ ...styles.title, color: 'white' }}>
          {t('here-you-will-meet')}
        </Text>
        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: 'white',
            marginTop: 20,
            marginLeft: 0,
          }}
        />

        <View style={styles.targetGroupsWrapper}>
          {targetGroupsItems?.map((item, index) => (
            <View key={item.title} style={{ width: targetGroupItemWidth }}>
              <View style={styles.targetGroupsItem}>
                <View style={styles.targetGroupsHeader}>
                  <Text style={{ ...styles.iconWhite }}>
                    {iconsMap[item.icon] || iconsMap.default}
                  </Text>
                  <Text style={styles.columnTitle}>{item.title}</Text>
                </View>

                <View>
                  {item.content.map((content, index) => (
                    <View key={index} style={{ marginBottom: 6 }}>
                      <View style={styles.rowSpaceBetween}>
                        <Text
                          style={{
                            ...styles.smallText,
                            fontSize: targetGroupsItems.length > 4 ? 11 : 14,
                            width: '80%',
                            textAlign: 'left',
                          }}
                        >
                          {content.title}
                        </Text>
                        <Text
                          style={{
                            ...styles.smallText,
                            fontSize: targetGroupsItems.length > 4 ? 11 : 14,
                            width: '20%',
                            textAlign: 'right',
                          }}
                        >
                          {content.percentage}%
                        </Text>
                      </View>
                      <View style={styles.progressBarContainer}>
                        <View
                          style={{
                            ...styles.progressBarFill,
                            width: `${content.percentage}%`,
                            backgroundColor:
                              event?.design?.themeColor ?? 'black',
                          }}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default WhoAndWhyPage;
